/**
 * 이미지/파일 업로드 규칙 상수
 *
 * DB 테이블: tb_file_info
 * - MDI_TYPE: 콘텐츠 도메인 분류
 * - GUBUN: MDI_TYPE 내 세부 분류
 * - FK_SEQ: 콘텐츠 연결 키 (boardSeq, clubSeq 등)
 *
 * 업로드 프로세스:
 * 1. fileApi.uploadTemp(file) → fileInfoSeq (임시 저장)
 * 2. 콘텐츠 생성/수정 → fkSeq 확보
 * 3. fileApi.linkFiles({ fileInfoSeqs, fkSeq, mdiType, gubun }) → 연결 확정
 */

// ==============================
// 파일 크기 제한 (bytes)
// ==============================

export const FILE_SIZE = {
  /** 3MB - 프로필/아바타 */
  SMALL: 3 * 1024 * 1024,
  /** 5MB - 동호회 이미지, 일반 이미지 */
  MEDIUM: 5 * 1024 * 1024,
  /** 10MB - 게시판 이미지 */
  LARGE: 10 * 1024 * 1024,
  /** 20MB - 엑셀/문서 */
  XLARGE: 20 * 1024 * 1024,
  /** 50MB - 첨부파일 (문서 + 이미지 혼합) */
  MAX: 50 * 1024 * 1024,
} as const

/**
 * 사진 한 장의 최대 원본 크기 — 2026-09-29 사용자 결정(발주 추가 36 개발사 메모):
 * «장당 5MB 까지 받고 자동으로 줄여 저장». 올리기 전에 기기에서 1200px·품질 0.8 로 줄인다(fileApi.uploadTemp).
 * 서버도 같은 기준으로 막는다(FileController.fileTempUpload).
 */
export const PHOTO_MAX_BYTES = FILE_SIZE.MEDIUM
export const PHOTO_MAX_MESSAGE = '사진은 장당 5MB까지 올릴 수 있습니다.'
export const isPhotoTooLarge = (f: File) => f.type.startsWith('image/') && f.size > PHOTO_MAX_BYTES

// ==============================
// 허용 파일 형식
// ==============================

export const ACCEPT = {
  /** 이미지만 (JPG, PNG, WebP, GIF) */
  IMAGE: 'image/jpeg,image/png,image/webp,image/gif',
  /** 이미지만 (JPG, PNG) - 프로필/대표 이미지 */
  IMAGE_STRICT: 'image/jpeg,image/png',
  /** 문서 (PDF, Word, HWP) */
  DOCUMENT: '.pdf,.doc,.docx,.hwp',
  /** 엑셀 */
  EXCEL: '.xlsx,.xls,.csv',
  /** 이미지 + 문서 (게시판 첨부) */
  BOARD_ATTACH: 'image/jpeg,image/png,image/webp,image/gif,.pdf,.doc,.docx',
  /** 모든 파일 */
  ALL: '*',
} as const

// ==============================
// MDI_TYPE + GUBUN 매핑
// ==============================
// ⛔ 2026-09-30 §7-H17 — 이 표는 «실제로 저장되는 값»과 같아야 한다(예전 값이 달라 따라 쓰면 결함이 커졌다).
//    · GUBUN 은 tb_file_info_mst 외래키다 — 마스터에 없는 값(FREE·RESTAURANT·SETTLEMENT …)은 저장이 실패한다.
//    · 읽는 쪽은 MDI_TYPE + FK_SEQ 로 찾는다. GUBUN 으로 다시 거르지 않는다(쓰는 화면마다 값이 달라 어긋난다 — §7-H1·H8).

export const UPLOAD_TYPE = {
  /** 사진게시판 이미지 */
  PHOTO_BOARD: { mdiType: 'CLUB_BOARD_PHOTO', gubun: 'PHOTO' },
  /** 공지사항 첨부파일 */
  NOTICE: { mdiType: 'CLUB_BOARD', gubun: 'NOTICE' },
  /** 자유게시판 첨부파일 */
  FREEBOARD: { mdiType: 'CLUB_BOARD', gubun: 'FREEBOARD' },
  /** 스폰서 기록 첨부파일 */
  SPONSOR: { mdiType: 'CLUB_BOARD', gubun: 'SPONSOR' },
  /** 동호회 프로필 이미지 */
  CLUB_PROFILE: { mdiType: 'CLUB_IMAGE', gubun: 'PROFILE' },
  /** 동호회 커버 이미지 */
  CLUB_COVER: { mdiType: 'CLUB_IMAGE', gubun: 'COVER' },
  /** 동호회 갤러리 이미지 */
  CLUB_GALLERY: { mdiType: 'CLUB_IMAGE', gubun: 'GALLERY' },
  /** 정산 영수증 이미지 — 서버가 항목(FK=itemSeq)에 연결한다(ClubSettlementServiceImpl) */
  SETTLEMENT_RECEIPT: { mdiType: 'SETTLEMENT_ITEM', gubun: 'RECEIPT' },
  /** 정산 엑셀 — 서버가 정산(FK=settlementSeq)에 연결한다. 2026-09-30 전에는 GUBUN SETTLEMENT(마스터에 없음)로 넣어 등록이 실패했다(§7-H9) */
  SETTLEMENT_EXCEL: { mdiType: 'SETTLEMENT', gubun: 'EXCEL' },
  /** 상품 이미지 */
  PRODUCT_IMAGE: { mdiType: 'PRODUCT_IMG', gubun: 'PRODUCT' },
  /** 상품 리뷰 이미지 */
  PRODUCT_REVIEW: { mdiType: 'PRODUCT_REVIEW', gubun: 'REVIEW' },
  /** 공동구매 썸네일 */
  GROUP_PURCHASE: { mdiType: 'GROUP_PURCHASE', gubun: 'THUMBNAIL' },
  /** 시상식/명예의전당 사진 — ⚠ 2026-09-30 현재 이 값으로 올리는 화면이 없다(명예의전당 사진 칸을 채우는 코드 없음 — §7-H14) */
  AWARD_PHOTO: { mdiType: 'AWARD_PHOTO', gubun: 'AWARD' },
  /** 행사 사진 — Vue 는 EVENT_PHOTO, CMS·5/9 이전 업로드는 PHOTO 로 저장돼 있다. 읽을 때는 MDI_TYPE 만 본다(§7-H8) */
  EVENT_PHOTO: { mdiType: 'EVENT_PHOTO', gubun: 'EVENT_PHOTO' },
} as const

// ==============================
// 화면별 업로드 규칙
// ==============================

export interface UploadRule {
  /** 업로드 타입 (mdiType + gubun) */
  type: { mdiType: string; gubun: string }
  /** 최대 파일 개수 (null = 무제한) */
  maxFiles: number | null
  /** 최대 파일 크기 (bytes) */
  maxSize: number
  /** 허용 파일 형식 (FileInput accept 속성용) */
  accept: string
  /** 권장 이미지 크기 (px) */
  recommendedSize?: string
  /** 설명 */
  description: string
}

/** 화면별 업로드 규칙 */
export const UPLOAD_RULES: Record<string, UploadRule> = {
  // ---- 게시판 ----
  PHOTO_BOARD: {
    type: UPLOAD_TYPE.PHOTO_BOARD,
    maxFiles: 5,
    maxSize: FILE_SIZE.MEDIUM,
    accept: ACCEPT.IMAGE,
    description: '사진게시판 이미지 (최대 5장, 장당 5MB)',
  },
  NOTICE_BOARD: {
    type: UPLOAD_TYPE.NOTICE,
    maxFiles: null,
    maxSize: FILE_SIZE.MAX,
    accept: ACCEPT.BOARD_ATTACH,
    description: '공지사항 첨부파일 (이미지 + 문서)',
  },
  FREE_BOARD: {
    type: UPLOAD_TYPE.FREEBOARD,
    maxFiles: null,
    maxSize: FILE_SIZE.MAX,
    accept: ACCEPT.BOARD_ATTACH,
    description: '자유게시판 첨부파일 (이미지 + 문서)',
  },
  SPONSOR_BOARD: {
    type: UPLOAD_TYPE.SPONSOR,
    maxFiles: null,
    maxSize: FILE_SIZE.MAX,
    accept: ACCEPT.BOARD_ATTACH,
    description: '스폰서 기록 첨부파일 (이미지 + 문서)',
  },

  // ---- 동호회 ----
  CLUB_PROFILE: {
    type: UPLOAD_TYPE.CLUB_PROFILE,
    maxFiles: 1,
    maxSize: FILE_SIZE.MEDIUM,
    accept: ACCEPT.IMAGE_STRICT,
    // 2026-10-02 10/1 §7-E3 — 화면 안내(ClubImagePicker «가로형 4:3, 1200×900 이상»)와 같게
    recommendedSize: '1200x900',
    description: '동호회 대표 이미지 (1장, JPG/PNG, 5MB)',
  },
  CLUB_COVER: {
    type: UPLOAD_TYPE.CLUB_COVER,
    maxFiles: 1,
    maxSize: FILE_SIZE.MEDIUM,
    accept: ACCEPT.IMAGE_STRICT,
    recommendedSize: '720x300',
    description: '동호회 커버 이미지 (1장, JPG/PNG, 5MB)',
  },
  CLUB_GALLERY: {
    type: UPLOAD_TYPE.CLUB_GALLERY,
    maxFiles: 20,
    maxSize: FILE_SIZE.LARGE,
    accept: ACCEPT.IMAGE,
    description: '동호회 갤러리 (최대 20장, 장당 5MB)',
  },

  // ---- 정산 ----
  SETTLEMENT_RECEIPT: {
    type: UPLOAD_TYPE.SETTLEMENT_RECEIPT,
    maxFiles: null,
    maxSize: FILE_SIZE.LARGE,
    accept: ACCEPT.IMAGE,
    description: '정산 영수증 이미지 (이미지만)',
  },
  SETTLEMENT_EXCEL: {
    type: UPLOAD_TYPE.SETTLEMENT_EXCEL,
    maxFiles: 1,
    maxSize: FILE_SIZE.XLARGE,
    accept: ACCEPT.EXCEL,
    description: '정산 엑셀 파일 (1장, 20MB)',
  },

  // ---- 쇼핑몰 ----
  PRODUCT_IMAGE: {
    type: UPLOAD_TYPE.PRODUCT_IMAGE,
    maxFiles: 10,
    maxSize: FILE_SIZE.LARGE,
    accept: ACCEPT.IMAGE,
    description: '상품 이미지 (최대 10장, 장당 10MB)',
  },
  PRODUCT_REVIEW: {
    type: UPLOAD_TYPE.PRODUCT_REVIEW,
    maxFiles: 6,
    maxSize: FILE_SIZE.LARGE,
    accept: ACCEPT.IMAGE,
    description: '상품 리뷰 이미지 (최대 6장, 장당 5MB)',
  },
  GROUP_PURCHASE_THUMBNAIL: {
    type: UPLOAD_TYPE.GROUP_PURCHASE,
    maxFiles: 1,
    maxSize: FILE_SIZE.MEDIUM,
    accept: ACCEPT.IMAGE_STRICT,
    recommendedSize: '400x300',
    description: '공동구매 썸네일 (1장, JPG/PNG, 5MB)',
  },

  // ---- 시상/명예의전당 ----
  AWARD_PHOTO: {
    type: UPLOAD_TYPE.AWARD_PHOTO,
    maxFiles: 10,
    maxSize: FILE_SIZE.MEDIUM,
    accept: ACCEPT.IMAGE,
    description: '시상식/명예의전당 사진 (최대 10장, 장당 5MB)',
  },

  // ---- 행사 사진 ----
  // 2026-10-02 (10/1 문서 §7-E5) — 실제 한도는 장당 5MB(fileApi.uploadTemp · 서버 FileController — 9/29 사용자 결정).
  //   10MB 로 두면 5~10MB 사진이 고를 때는 통과하고 올릴 때 막혔다.
  EVENT_PHOTO: {
    type: UPLOAD_TYPE.EVENT_PHOTO,
    maxFiles: 10,
    maxSize: FILE_SIZE.MEDIUM,
    accept: ACCEPT.IMAGE,
    description: '행사 사진 (최대 10장, 장당 5MB)',
  },
}

// ==============================
// 유틸리티 함수
// ==============================

/**
 * 파일 크기를 사람이 읽을 수 있는 형식으로 변환
 */
export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes}B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)}KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)}MB`
}

/**
 * 파일 유효성 검사
 * @returns 에러 메시지 (유효하면 null)
 */
export function validateFile(file: File, rule: UploadRule): string | null {
  // 크기 검사
  if (file.size > rule.maxSize) {
    return `파일 크기가 ${formatFileSize(rule.maxSize)}를 초과합니다. (현재: ${formatFileSize(file.size)})`
  }

  // 확장자 검사
  if (rule.accept !== '*') {
    const acceptList = rule.accept.split(',').map(a => a.trim().toLowerCase())
    const fileExt = '.' + (file.name.split('.').pop() || '').toLowerCase()
    const fileMime = file.type.toLowerCase()

    const isAccepted = acceptList.some(accept => {
      if (accept.startsWith('.')) {
        return fileExt === accept
      }
      if (accept.endsWith('/*')) {
        return fileMime.startsWith(accept.replace('/*', '/'))
      }
      return fileMime === accept
    })

    if (!isAccepted) {
      return `허용되지 않는 파일 형식입니다. (허용: ${rule.accept})`
    }
  }

  return null
}

/**
 * 파일 목록 유효성 검사
 * @returns 에러 메시지 배열 (모두 유효하면 빈 배열)
 */
export function validateFiles(files: File[], rule: UploadRule): string[] {
  const errors: string[] = []

  // 개수 검사
  if (rule.maxFiles !== null && files.length > rule.maxFiles) {
    errors.push(`최대 ${rule.maxFiles}개까지 업로드 가능합니다. (선택: ${files.length}개)`)
  }

  // 개별 파일 검사
  files.forEach((file) => {
    const error = validateFile(file, rule)
    if (error) {
      errors.push(`[${file.name}] ${error}`)
    }
  })

  return errors
}
